import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AppConfig, environment } from '../../../environments/environment';
import { supabase } from '../../core/supabase/supabase.client';
import { AuthService } from '../../services/auth/auth.service';
import { SnackbarService } from '../../services/design/snackbar.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './reset-password.component.html',
})
export class ResetPasswordComponent implements OnInit {
  readonly #auth = inject(AuthService);
  readonly #router = inject(Router);
  readonly #snackbar = inject(SnackbarService);

  appConfig: AppConfig = environment.appConfig;
  password = '';
  confirmPassword = '';
  showPassword = false;
  showConfirmPassword = false;
  checkingSession = true;
  validSession = false;
  submitting = false;

  async ngOnInit() {
    try {
      await this.#auth.initialize();
      const { data, error } = await supabase.auth.getSession();
      this.validSession =
        !error && !!data.session && this.#auth.isLoggedIn();
    } catch {
      this.validSession = false;
    } finally {
      this.checkingSession = false;
    }
  }

  async updatePassword() {
    if (this.password.length < 6) {
      this.#snackbar.error('Password must be at least 6 characters');
      return;
    }

    if (this.password !== this.confirmPassword) {
      this.#snackbar.error('Passwords do not match');
      return;
    }

    this.submitting = true;
    try {
      await this.#auth.updatePassword(this.password);
      await this.#auth.logout();
      this.#snackbar.success(
        'Password updated successfully. Sign in with your new password.'
      );
      await this.#router.navigate(['/login']);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to update password. Request a new reset link.';
      this.#snackbar.error(message);
    } finally {
      this.submitting = false;
    }
  }
}
